import { useEffect } from 'react'
import { useRouteLoaderData } from 'react-router'
import { create } from 'zustand'
import type { Route } from '#app/+types/app/+types/root'
import { useCurrentWorkspace } from '#app/hooks/use-current-workspace.ts'
import type { RootLoaderType } from '#app/root.tsx'
import type { FeatureFlagKey } from './auth.server'

// Zustand store for user data
type UserStore = {
	id: string
	name: string
	email: string
	setUser: (user: { id: string; name: string; email: string }) => void
	clearUser: () => void
}

export const useUserStore = create<UserStore>()((set) => ({
	id: '',
	name: '',
	email: '',
	setUser: (user) => set(user),
	clearUser: () => set({ id: '', name: '', email: '' }),
}))

// Route
function isUser(user: any): user is Route.ComponentProps['loaderData']['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

export function useOptionalUser() {
	const data = useRouteLoaderData<RootLoaderType>('root')
	const setUser = useUserStore((store) => store.setUser)
	const clearUser = useUserStore((store) => store.clearUser)

	// Sync user data with the store
	useEffect(() => {
		if (data && isUser(data.user) && data.user) {
			setUser({
				id: data.user.id,
				name: data.user.name,
				email: data.user.email,
			})
		} else {
			clearUser()
		}
	}, [data, setUser, clearUser])

	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}

	const currentWorkspace = useCurrentWorkspace()
	const currentRole = maybeUser.workspaces.find(
		(workspace) => workspace.workspaceId === currentWorkspace?.id,
	)?.role
	return {
		...maybeUser,
		currentRole,
	}
}

type Action = 'create' | 'read' | 'update' | 'delete'
type Entity = 'user' | 'note'
type Access = 'own' | 'any' | 'own,any' | 'any,own'
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

export function userHasPermission(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some((role) =>
		role.permissions.some(
			(permission) =>
				permission.entity === entity &&
				permission.action === action &&
				(!access || access.includes(permission.access)),
		),
	)
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}

export function useFeatureFlags() {
	const user = useOptionalUser()
	const currentWorkspace = useCurrentWorkspace()
	const workspaceFlags = currentWorkspace?.featureFlags ?? []
	const userFlags = user?.featureFlags ?? []
	return new Set([...userFlags, ...workspaceFlags])
}

export function useHasFeatureFlag(flag: string) {
	const flags = useFeatureFlags()
	return flags.has(flag as any)
}

export function useHasFeatureFlagKey(flag: FeatureFlagKey) {
	const flags = useFeatureFlags()
	return flags.has(flag)
}
